import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Textarea,
  useDisclosure,
  useToast,
  Container,
  Divider,
  Tooltip,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
  Code,
  Link,
  Progress
} from '@chakra-ui/react';
import {
  FaUpload,
  FaFileExcel,
  FaFileCsv,
  FaDownload,
  FaEye,
  FaEdit,
  FaCheck,
  FaTimes,
  FaClock,
  FaSearch,
  FaCalendarAlt,
  FaChartLine,
  FaFileInvoiceDollar,
  FaExternalLinkAlt,
  FaArrowRight,
  FaDatabase
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const AdminUploadManager = () => {
  const [uploads, setUploads] = useState([]);
  const [filteredUploads, setFilteredUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [processingNotes, setProcessingNotes] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inReview: 0,
    processed: 0,
    rejected: 0
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const { isOpen: isImportOpen, onOpen: onImportOpen, onClose: onImportClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);

  useEffect(() => {
    fetchUploads();
  }, []);

  useEffect(() => {
    filterUploads();
    setCurrentPage(1); // Reset to first page when filters change
  }, [uploads, searchTerm, statusFilter]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredUploads.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUploads = filteredUploads.slice(startIndex, startIndex + itemsPerPage);

  const fetchUploads = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('test_directory_uploads')
        .select('*')
        .order('uploaded_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        // Check if it's a table/permission error
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          toast({
            title: 'Table Not Found',
            description: 'The test_directory_uploads table does not exist. Please run the database migration.',
            status: 'error',
            duration: 8000,
            isClosable: true,
          });
        } else if (error.code === '42501' || error.message?.includes('permission')) {
          toast({
            title: 'Permission Denied',
            description: 'You do not have permission to access uploads. Please contact an administrator.',
            status: 'error',
            duration: 8000,
            isClosable: true,
          });
        } else {
          throw error;
        }
        // Set empty arrays to prevent further errors
        setUploads([]);
        calculateStats([]);
        return;
      }

      setUploads(data || []);
      calculateStats(data || []);
    } catch (error) {
      console.error('Error fetching uploads:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      toast({
        title: 'Error',
        description: `Failed to fetch uploads: ${errorMessage}. Please check the console for details.`,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
      // Set empty arrays to prevent further errors
      setUploads([]);
      calculateStats([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (uploadsData) => {
    const stats = {
      total: uploadsData.length,
      pending: uploadsData.filter(u => u.upload_status === 'pending_review').length,
      inReview: uploadsData.filter(u => u.upload_status === 'in_review').length,
      processed: uploadsData.filter(u => u.upload_status === 'processed').length,
      rejected: uploadsData.filter(u => u.upload_status === 'rejected').length
    };
    setStats(stats);
  };

  const filterUploads = () => {
    let filtered = uploads;

    if (searchTerm) {
      filtered = filtered.filter(upload =>
        upload.lab_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        upload.file_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(upload => upload.upload_status === statusFilter);
    }

    setFilteredUploads(filtered);
  };

  const handleStatusUpdate = async (uploadId, newStatus, notes = '') => {
    try {
      const { error } = await supabase
        .from('test_directory_uploads')
        .update({
          upload_status: newStatus,
          processing_notes: notes,
          reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', uploadId);

      if (error) throw error;

      await fetchUploads();
      
      toast({
        title: 'Status Updated',
        description: `Upload status changed to ${newStatus.replace('_', ' ')}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onClose();
      setSelectedUpload(null);
      setProcessingNotes('');
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const openProcessingModal = (upload) => {
    setSelectedUpload(upload);
    setProcessingNotes(upload.processing_notes || '');
    onOpen();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_review':
        return 'yellow';
      case 'in_review':
        return 'blue';
      case 'processed':
        return 'green';
      case 'rejected':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending_review':
        return 'Pending Review';
      case 'in_review':
        return 'In Review';
      case 'processed':
        return 'Processed';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName || typeof fileName !== 'string') {
      return FaUpload;
    }
    const extension = fileName.toLowerCase().split('.').pop();
    switch (extension) {
      case 'csv':
      case 'tsv':
        return FaFileCsv;
      case 'xlsx':
      case 'xls':
        return FaFileExcel;
      default:
        return FaUpload;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // CSV parsing configuration (reused from AdminTestTypesUpload)
  const CSV_MAPPINGS = {
    'TEST CODE': 'code',
    'test code': 'code',
    'CPT CODES': 'cpt_codes',
    'cpt codes': 'cpt_codes',
    'Panel Name ': 'panel_name',
    'Panel Name': 'panel_name',
    'PANEL NAME': 'panel_name',
    'panel name': 'panel_name',
    'Test Name ': 'test_name',
    'Test Name': 'test_name',
    'TEST NAME': 'test_name',
    'test name': 'test_name',
    'INCLUDES TESTS': 'description',
    'includes tests': 'description',
    'INCLUDES': 'description',
    'includes': 'description',
    'PATIENT PREPARATION': 'patient_preparation',
    'patient preparation': 'patient_preparation',
    'ALTERNATIVE NAME': 'alternative_name',
    'alternative name': 'alternative_name',
    'Methodology': 'methodology',
    'METHODOLOGY': 'methodology',
    'Reference Range(s)': 'reference_range',
    'REFERENCE RANGE': 'reference_range',
    'PREFERRED SPECIMEN': 'preferred_specimen',
    'preferred specimen': 'preferred_specimen',
    'ALTERNATIVE SPECIMEN': 'alternative_specimen',
    'alternative specimen': 'alternative_specimen',
    'MINIMUM VOLUME': 'minimum_volume',
    'minimum volume': 'minimum_volume',
    'COLLECTION INSTRUCTION': 'interfering_factors',
    'collection instruction': 'interfering_factors',
    'TRANSPORT CONTAINER': 'tube_top_color',
    'transport container': 'tube_top_color',
    'TRANSPORT TEMPERATURE': 'temperature_requirements',
    'transport temperature': 'temperature_requirements',
    'SPECIMEN STABILITY': 'stability_requirements',
    'specimen stability': 'stability_requirements',
    'Clinical Significance ': 'clinical_significance',
    'Clinical Significance': 'clinical_significance',
    'CLINICAL SIGNIFICANCE': 'clinical_significance',
    'Reject Criteria ': 'reject_criteria',
    'Reject Criteria': 'reject_criteria',
    'REJECT CRITERIA': 'reject_criteria',
    'PRICE': 'cash_price',
    'price': 'cash_price',
    'CASH PRICE': 'cash_price',
    'cash price': 'cash_price',
    'LAB CODE': 'lab_code',
    'lab code': 'lab_code',
    'LAB TEST CODE': 'lab_code',
    'lab test code': 'lab_code'
  };

  // Parse CSV file content
  const parseCSV = (csvText) => {
    const records = [];
    let currentRecord = [];
    let currentField = '';
    let inQuotes = false;
    let i = 0;
    
    while (i < csvText.length) {
      const char = csvText[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        currentRecord.push(currentField.trim());
        currentField = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        currentRecord.push(currentField.trim());
        if (currentRecord.some(field => field.length > 0)) {
          records.push(currentRecord);
        }
        currentRecord = [];
        currentField = '';
        if (char === '\r' && csvText[i + 1] === '\n') {
          i++;
        }
      } else {
        currentField += char;
      }
      i++;
    }
    
    if (currentRecord.length > 0 || currentField.length > 0) {
      currentRecord.push(currentField.trim());
      if (currentRecord.some(field => field.length > 0)) {
        records.push(currentRecord);
      }
    }
    
    if (records.length <= 1) return [];
    
    const rawHeaders = records[0].map(h => h.replace(/^"|"$/g, '').trim());
    const headerPositions = {};
    const headers = [];
    rawHeaders.forEach((header, index) => {
      if (header && header.trim()) {
        headers.push(header);
        headerPositions[header] = index;
      }
    });
    
    const data = [];
    
    for (let recordIndex = 1; recordIndex < records.length; recordIndex++) {
      const record = records[recordIndex];
      if (!record.some(field => field && field.trim())) continue;
      
      const row = {};
      headers.forEach((header) => {
        const originalIndex = headerPositions[header];
        // Safety check: ensure record has enough columns
        let value = (record[originalIndex] !== undefined ? record[originalIndex] : '').trim();
        
        // Apply length limits for VARCHAR(50) fields
        const lowerHeader = header.toLowerCase();
        if ((lowerHeader.includes('code') || lowerHeader.includes('tube') || 
             lowerHeader.includes('color') || lowerHeader.includes('cpt') ||
             lowerHeader.includes('methodology')) && value.length > 47) {
          value = value.substring(0, 47) + '...';
        }
        
        row[header] = value;
      });
      
      const mappedRow = {};
      Object.keys(row).forEach(key => {
        const mappedKey = CSV_MAPPINGS[key] || key.toLowerCase().replace(/\s+/g, '_');
        mappedRow[mappedKey] = row[key];
      });
      
      const panelName = mappedRow.panel_name || '';
      const testName = mappedRow.test_name || '';
      
      if (panelName && panelName.trim()) {
        mappedRow.name = panelName.trim();
        mappedRow.record_type = 'panel';
        data.push(mappedRow);
      } else if (testName && testName.trim()) {
        mappedRow.name = testName.trim();
        mappedRow.record_type = 'test';
        data.push(mappedRow);
      }
    }
    
    return data;
  };

  // Import tests/panels from uploaded file
  const handleImportFromUpload = async (upload) => {
    if (!upload || !upload.file_url || !upload.lab_id) {
      toast({
        title: 'Error',
        description: 'Missing file URL or lab ID. Cannot import.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setImporting(true);
    setImportProgress(0);
    setImportResults(null);
    onImportOpen();

    try {
      // Step 1: Download file
      setImportProgress(10);
      const response = await fetch(upload.file_url);
      if (!response.ok) throw new Error('Failed to download file');
      
      const fileExtension = upload.file_name.toLowerCase().split('.').pop();
      if (fileExtension !== 'csv' && fileExtension !== 'txt') {
        throw new Error('Only CSV files are supported for import. Excel support coming soon.');
      }

      const csvText = await response.text();
      setImportProgress(30);

      // Step 2: Parse CSV
      const parsedData = parseCSV(csvText);
      if (parsedData.length === 0) {
        throw new Error('No valid test or panel data found in file');
      }

      setImportProgress(40);

      // Step 3: Import tests and panels
      const results = {
        testsCreated: 0,
        panelsCreated: 0,
        testOfferingsCreated: 0,
        panelOfferingsCreated: 0,
        errors: []
      };

      const labId = upload.lab_id;

      for (let i = 0; i < parsedData.length; i++) {
        const row = parsedData[i];
        const progress = 40 + (i / parsedData.length) * 50;
        setImportProgress(progress);

        try {
          if (row.record_type === 'test') {
            // Create or find test
            const { data: existingTest } = await supabase
              .from('tests')
              .select('id')
              .eq('name', row.name)
              .maybeSingle();

            // Safety check: ensure name exists
            if (!row.name || !row.name.trim()) {
              throw new Error('Test name is required');
            }

            let testId;
            if (existingTest) {
              testId = existingTest.id;
            } else {
              const testData = {
                name: (row.name || '').substring(0, 255).trim(),
                code: (row.code || '').substring(0, 50).trim() || null,
                description: (row.description || '').trim() || null,
                category: (row.category || '').substring(0, 100).trim() || null,
                cash_price: row.cash_price ? (parseFloat(row.cash_price) || 0) : 0,
                tube_top_color: (row.tube_top_color || '').substring(0, 50).trim() || null,
                preferred_specimen: (row.preferred_specimen || '').substring(0, 200).trim() || null,
                minimum_volume: (row.minimum_volume || '').substring(0, 100).trim() || null,
                alternative_name: (row.alternative_name || '').substring(0, 500).trim() || null,
                alternative_specimen: (row.alternative_specimen || '').substring(0, 200).trim() || null,
                patient_preparation: (row.patient_preparation || '').trim() || null,
                interfering_factors: (row.interfering_factors || '').trim() || null,
                temperature_requirements: (row.temperature_requirements || '').substring(0, 200).trim() || null,
                stability_requirements: (row.stability_requirements || '').substring(0, 200).trim() || null,
                methodology: (row.methodology || '').substring(0, 50).trim() || null,
                reference_range: (row.reference_range || '').substring(0, 200).trim() || null,
                clinical_significance: (row.clinical_significance || '').trim() || null,
                reject_criteria: (row.reject_criteria || '').substring(0, 200).trim() || null,
                cpt_codes: (row.cpt_codes || '').substring(0, 50).trim() || null,
                is_active: true
              };

              const { data: newTest, error: testError } = await supabase
                .from('tests')
                .insert([testData])
                .select()
                .single();

              if (testError) throw testError;
              testId = newTest.id;
              results.testsCreated++;
            }

            // Create lab test offering
            const labTestCode = (row.lab_code || row.code || '').substring(0, 100).trim() || null;
            const labCashPrice = row.cash_price ? (parseFloat(row.cash_price) || null) : null;
            
            const { error: offeringError } = await supabase
              .from('lab_test_offerings')
              .upsert({
                lab_id: labId,
                test_id: testId,
                is_offered: true,
                lab_test_code: labTestCode,
                lab_cash_price: labCashPrice,
                lab_notes: null
              }, { onConflict: 'lab_id,test_id' });

            if (offeringError) throw offeringError;
            results.testOfferingsCreated++;

          } else if (row.record_type === 'panel') {
            // Create or find panel
            const { data: existingPanel } = await supabase
              .from('panels')
              .select('id')
              .eq('name', row.name)
              .maybeSingle();

            // Safety check: ensure name exists
            if (!row.name || !row.name.trim()) {
              throw new Error('Panel name is required');
            }

            let panelId;
            if (existingPanel) {
              panelId = existingPanel.id;
            } else {
              const panelData = {
                name: (row.name || '').substring(0, 255).trim(),
                code: (row.code || '').substring(0, 50).trim() || null,
                description: (row.description || '').trim() || null,
                category: (row.category || '').substring(0, 100).trim() || null,
                cash_price: row.cash_price ? (parseFloat(row.cash_price) || 0) : 0,
                tube_top_color: (row.tube_top_color || '').substring(0, 50).trim() || null,
                preferred_specimen: (row.preferred_specimen || '').substring(0, 200).trim() || null,
                minimum_volume: (row.minimum_volume || '').substring(0, 100).trim() || null,
                alternative_name: (row.alternative_name || '').substring(0, 500).trim() || null,
                alternative_specimen: (row.alternative_specimen || '').substring(0, 200).trim() || null,
                patient_preparation: (row.patient_preparation || '').trim() || null,
                interfering_factors: (row.interfering_factors || '').trim() || null,
                temperature_requirements: (row.temperature_requirements || '').substring(0, 200).trim() || null,
                stability_requirements: (row.stability_requirements || '').substring(0, 200).trim() || null,
                methodology: (row.methodology || '').substring(0, 50).trim() || null,
                reference_range: (row.reference_range || '').substring(0, 200).trim() || null,
                clinical_significance: (row.clinical_significance || '').trim() || null,
                reject_criteria: (row.reject_criteria || '').substring(0, 200).trim() || null,
                cpt_codes: (row.cpt_codes || '').substring(0, 50).trim() || null,
                is_active: true
              };

              const { data: newPanel, error: panelError } = await supabase
                .from('panels')
                .insert([panelData])
                .select()
                .single();

              if (panelError) throw panelError;
              panelId = newPanel.id;
              results.panelsCreated++;
            }

            // Create lab panel offering
            const labPanelCode = (row.lab_code || row.code || '').substring(0, 100).trim() || null;
            const labCashPrice = row.cash_price ? (parseFloat(row.cash_price) || null) : null;
            
            const { error: offeringError } = await supabase
              .from('lab_panel_offerings')
              .upsert({
                lab_id: labId,
                panel_id: panelId,
                is_offered: true,
                lab_panel_code: labPanelCode,
                lab_cash_price: labCashPrice,
                lab_notes: null
              }, { onConflict: 'lab_id,panel_id' });

            if (offeringError) throw offeringError;
            results.panelOfferingsCreated++;
          }
        } catch (error) {
          console.error(`Error importing row ${i + 1}:`, error);
          results.errors.push(`Row ${i + 1} (${row.name || 'Unknown'}): ${error.message}`);
        }
      }

      setImportProgress(100);
      setImportResults(results);

      // Update upload status to processed
      await supabase
        .from('test_directory_uploads')
        .update({
          upload_status: 'processed',
          reviewed_at: new Date().toISOString(),
          processing_notes: `Imported ${results.testsCreated} tests, ${results.panelsCreated} panels. Created ${results.testOfferingsCreated} test offerings, ${results.panelOfferingsCreated} panel offerings.`
        })
        .eq('id', upload.id);

      await fetchUploads();

      toast({
        title: 'Import Successful',
        description: `Imported ${results.testsCreated + results.panelsCreated} items. ${results.testOfferingsCreated + results.panelOfferingsCreated} offerings created.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: 'Import Failed',
        description: error.message || 'Failed to import tests/panels from file',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setImportResults({ errors: [error.message] });
    } finally {
      setImporting(false);
    }
  };

  // File download handler
  const handleDownload = async (upload) => {
    if (!upload.file_url) {
      toast({
        title: 'Error',
        description: 'File URL not available',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Open file in new tab for download
      window.open(upload.file_url, '_blank');
      toast({
        title: 'Download Started',
        description: `Downloading ${upload.file_name}`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: 'Download Failed',
        description: 'Could not download file. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // File preview handler
  const handlePreview = async (upload) => {
    if (!upload.file_url) {
      toast({
        title: 'Error',
        description: 'File URL not available for preview',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSelectedUpload(upload);
    setPreviewLoading(true);
    setFilePreview(null);
    onPreviewOpen();

    try {
      // For now, we'll show the file URL and allow opening in new tab
      // In a full implementation, you could parse CSV/Excel files here
      setFilePreview({
        url: upload.file_url,
        type: upload.file_type,
        name: upload.file_name
      });
    } catch (error) {
      console.error('Error loading preview:', error);
      toast({
        title: 'Preview Error',
        description: 'Could not load file preview',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Container maxW="7xl" py={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex align="center">
          <Icon as={FaFileInvoiceDollar} boxSize={6} color="blue.500" mr={3} />
          <Box>
            <Heading size="lg" mb={2} color="gray.800">Test Directory Uploads</Heading>
            <Text color="gray.600">
              Manage and review test directory uploads from laboratory partners
            </Text>
          </Box>
        </Flex>

        {/* Stats Cards */}
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" boxShadow="sm">
          <CardBody>
            <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
              <Card bg="blue.50" borderWidth="1px" borderColor="blue.200" borderRadius="lg" p={4}>
                <Stat>
                  <Flex align="center" mb={2}>
                    <Icon as={FaChartLine} color="blue.600" mr={2} />
                    <StatLabel fontSize="sm" color="gray.600" fontWeight="medium">Total Uploads</StatLabel>
                  </Flex>
                  <StatNumber fontSize="2xl" fontWeight="bold" color="gray.800">{stats.total}</StatNumber>
                  <StatHelpText fontSize="xs" color="gray.500">All time</StatHelpText>
                </Stat>
              </Card>
              
              <Card bg="yellow.50" borderWidth="1px" borderColor="yellow.200" borderRadius="lg" p={4}>
                <Stat>
                  <Flex align="center" mb={2}>
                    <Icon as={FaClock} color="yellow.600" mr={2} />
                    <StatLabel fontSize="sm" color="gray.600" fontWeight="medium">Pending Review</StatLabel>
                  </Flex>
                  <StatNumber fontSize="2xl" fontWeight="bold" color="yellow.600">{stats.pending}</StatNumber>
                  <StatHelpText fontSize="xs" color="gray.500">Needs attention</StatHelpText>
                </Stat>
              </Card>
              
              <Card bg="blue.50" borderWidth="1px" borderColor="blue.200" borderRadius="lg" p={4}>
                <Stat>
                  <Flex align="center" mb={2}>
                    <Icon as={FaEye} color="blue.600" mr={2} />
                    <StatLabel fontSize="sm" color="gray.600" fontWeight="medium">In Review</StatLabel>
                  </Flex>
                  <StatNumber fontSize="2xl" fontWeight="bold" color="blue.600">{stats.inReview}</StatNumber>
                  <StatHelpText fontSize="xs" color="gray.500">Being processed</StatHelpText>
                </Stat>
              </Card>
              
              <Card bg="green.50" borderWidth="1px" borderColor="green.200" borderRadius="lg" p={4}>
                <Stat>
                  <Flex align="center" mb={2}>
                    <Icon as={FaCheck} color="green.600" mr={2} />
                    <StatLabel fontSize="sm" color="gray.600" fontWeight="medium">Processed</StatLabel>
                  </Flex>
                  <StatNumber fontSize="2xl" fontWeight="bold" color="green.600">{stats.processed}</StatNumber>
                  <StatHelpText fontSize="xs" color="gray.500">Completed</StatHelpText>
                </Stat>
              </Card>
              
              <Card bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="lg" p={4}>
                <Stat>
                  <Flex align="center" mb={2}>
                    <Icon as={FaTimes} color="red.600" mr={2} />
                    <StatLabel fontSize="sm" color="gray.600" fontWeight="medium">Rejected</StatLabel>
                  </Flex>
                  <StatNumber fontSize="2xl" fontWeight="bold" color="red.600">{stats.rejected}</StatNumber>
                  <StatHelpText fontSize="xs" color="gray.500">Declined</StatHelpText>
                </Stat>
              </Card>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Filters */}
        <Card boxShadow="sm" borderRadius="lg">
          <CardBody p={4}>
            <HStack spacing={4} wrap="wrap">
              <InputGroup maxW="400px" flex={1} minW="250px">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search lab name or file..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                />
              </InputGroup>
              
              <Select 
                maxW="200px" 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                bg="white"
                borderColor="gray.300"
              >
                <option value="all">All Statuses</option>
                <option value="pending_review">Pending Review</option>
                <option value="in_review">In Review</option>
                <option value="processed">Processed</option>
                <option value="rejected">Rejected</option>
              </Select>

              <Button 
                leftIcon={<Icon as={FaCalendarAlt} />}
                variant="outline"
                onClick={fetchUploads}
                isLoading={loading}
              >
                Refresh
              </Button>
            </HStack>
          </CardBody>
        </Card>

        {/* Pagination Info */}
        {filteredUploads.length > 0 && (
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <Text color="gray.600" fontSize="sm" fontWeight="medium">
              Showing <Text as="span" fontWeight="bold" color="gray.800">{paginatedUploads.length}</Text> of{' '}
              <Text as="span" fontWeight="bold" color="gray.800">{filteredUploads.length}</Text> uploads
              {filteredUploads.length !== uploads.length && (
                <Text as="span" color="gray.500">
                  {' '}(filtered from {uploads.length} total)
                </Text>
              )}
            </Text>
            {totalPages > 1 && (
              <HStack spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                >
                  Previous
                </Button>
                <Text fontSize="sm" fontWeight="medium" color="gray.700" px={3}>
                  Page {currentPage} of {totalPages}
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                >
                  Next
                </Button>
              </HStack>
            )}
          </Flex>
        )}

        {/* Uploads Table */}
        <Card boxShadow="sm" borderRadius="lg" overflow="hidden">
          <CardHeader bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
            <Heading size="md" color="gray.800">Recent Uploads ({filteredUploads.length})</Heading>
          </CardHeader>
          <CardBody p={0}>
            <Box overflowX="auto">
              <Table variant="simple" size="md">
                <Thead bg="gray.50">
                  <Tr>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm">Lab & File</Th>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm">File Details</Th>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm">Status</Th>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm">Upload Date</Th>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {loading ? (
                    <Tr>
                      <Td colSpan={5} textAlign="center" py={12}>
                        <VStack spacing={4}>
                          <Spinner size="lg" color="blue.500" thickness="4px" />
                          <Text color="gray.600" fontWeight="medium">Loading uploads...</Text>
                        </VStack>
                      </Td>
                    </Tr>
                  ) : paginatedUploads.length === 0 ? (
                    <Tr>
                      <Td colSpan={5} textAlign="center" py={12}>
                        <VStack spacing={3}>
                          <Icon as={FaFileInvoiceDollar} boxSize={12} color="gray.300" />
                          <Text color="gray.500" fontSize="md" fontWeight="medium">No uploads found</Text>
                          {searchTerm && (
                            <Text color="gray.400" fontSize="sm">
                              Try adjusting your search or filter criteria
                            </Text>
                          )}
                        </VStack>
                      </Td>
                    </Tr>
                  ) : (
                    paginatedUploads.map((upload, index) => (
                      <Tr 
                        key={upload.id}
                        _hover={{ bg: 'gray.50' }}
                        bg={index % 2 === 0 ? 'white' : 'gray.25'}
                        transition="background 0.2s"
                      >
                        <Td py={4}>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="bold" color="gray.800" fontSize="sm">{upload.lab_name}</Text>
                            <HStack>
                              <Icon as={getFileIcon(upload.file_name || '')} color="blue.500" />
                              <Text fontSize="sm" color="gray.600">
                                {upload.file_name || 'Unknown file'}
                              </Text>
                            </HStack>
                          </VStack>
                        </Td>
                        <Td py={4}>
                          <VStack align="start" spacing={1}>
                            <Badge colorScheme="blue" variant="subtle" fontSize="xs" px={2} py={1}>
                              {upload.file_type || 'Unknown'}
                            </Badge>
                            <Text fontSize="xs" color="gray.500">
                              {formatFileSize(upload.file_size)}
                            </Text>
                          </VStack>
                        </Td>
                        <Td py={4}>
                          <Badge 
                            colorScheme={getStatusColor(upload.upload_status)} 
                            fontSize="xs"
                            px={2}
                            py={1}
                            variant={upload.upload_status === 'processed' ? 'solid' : 'subtle'}
                          >
                            {getStatusLabel(upload.upload_status)}
                          </Badge>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" color="gray.700">{formatDate(upload.uploaded_at)}</Text>
                          {upload.reviewed_at && (
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              Reviewed: {formatDate(upload.reviewed_at)}
                            </Text>
                          )}
                        </Td>
                        <Td py={4}>
                          <HStack spacing={2} flexWrap="wrap">
                            <Tooltip label="Preview File" hasArrow>
                              <Button
                                size="sm"
                                variant="outline"
                                leftIcon={<FaEye />}
                                onClick={() => handlePreview(upload)}
                                fontSize="xs"
                              >
                                View
                              </Button>
                            </Tooltip>
                            <Tooltip label="Download File" hasArrow>
                              <Button
                                size="sm"
                                variant="outline"
                                leftIcon={<FaDownload />}
                                onClick={() => handleDownload(upload)}
                                fontSize="xs"
                              >
                                Download
                              </Button>
                            </Tooltip>
                            <Tooltip label="Process & Update Status" hasArrow>
                              <Button
                                size="sm"
                                colorScheme="blue"
                                leftIcon={<FaEdit />}
                                onClick={() => openProcessingModal(upload)}
                                fontSize="xs"
                                fontWeight="semibold"
                              >
                                Process
                              </Button>
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
      </VStack>

      {/* Processing Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg="blue.50" borderBottom="1px solid" borderColor="gray.200">
            <Flex align="center">
              <Icon as={FaEdit} color="blue.600" mr={2} />
              <Text>Process Upload: {selectedUpload?.file_name}</Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={6} align="stretch">
              <Card bg="gray.50" p={4}>
                <VStack align="stretch" spacing={3}>
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>Lab Name</Text>
                    <Text fontSize="md" fontWeight="bold" color="gray.800">{selectedUpload?.lab_name}</Text>
                  </Box>
                  <Divider />
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>File Information</Text>
                    <HStack spacing={2} mb={1}>
                      <Icon as={getFileIcon(selectedUpload?.file_name || '')} color="blue.500" />
                      <Text fontSize="sm" color="gray.700">{selectedUpload?.file_name || 'Unknown file'}</Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">
                      {formatFileSize(selectedUpload?.file_size || 0)} • {selectedUpload?.file_type || 'Unknown type'}
                    </Text>
                  </Box>
                  <Divider />
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>Upload Date</Text>
                    <Text fontSize="sm" color="gray.700">
                      {selectedUpload && formatDate(selectedUpload.uploaded_at)}
                    </Text>
                  </Box>
                  {selectedUpload?.reviewed_at && (
                    <>
                      <Divider />
                      <Box>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>Last Reviewed</Text>
                        <Text fontSize="sm" color="gray.700">
                          {formatDate(selectedUpload.reviewed_at)}
                        </Text>
                      </Box>
                    </>
                  )}
                </VStack>
              </Card>

              <Box>
                <Text fontWeight="semibold" mb={2} color="gray.700">Current Status</Text>
                <Badge 
                  colorScheme={getStatusColor(selectedUpload?.upload_status)} 
                  fontSize="sm"
                  px={3}
                  py={1}
                  variant={selectedUpload?.upload_status === 'processed' ? 'solid' : 'subtle'}
                >
                  {getStatusLabel(selectedUpload?.upload_status)}
                </Badge>
              </Box>

              <Box>
                <Text fontWeight="semibold" mb={2} color="gray.700">Processing Notes</Text>
                <Textarea
                  placeholder="Add notes about the processing, formatting changes, or rejection reasons. These notes will be visible to admins and can be shared with the lab if needed."
                  value={processingNotes}
                  onChange={(e) => setProcessingNotes(e.target.value)}
                  rows={5}
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                />
                {selectedUpload?.processing_notes && (
                  <Alert status="info" mt={2} borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>
                      <Text fontWeight="semibold" mb={1}>Previous Notes:</Text>
                      <Text>{selectedUpload.processing_notes}</Text>
                    </AlertDescription>
                  </Alert>
                )}
              </Box>

              {selectedUpload?.file_url && selectedUpload?.file_name && 
               (selectedUpload.file_name.toLowerCase().endsWith('.csv') || 
                selectedUpload.file_name.toLowerCase().endsWith('.txt')) && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    <Text fontWeight="semibold" mb={2}>Import Tests & Panels</Text>
                    <Text fontSize="sm" mb={3}>
                      This file can be imported to automatically create tests/panels and lab offerings. 
                      Click "Import Tests" below to parse the CSV and import into the catalog.
                    </Text>
                    <Button
                      size="sm"
                      colorScheme="purple"
                      leftIcon={<FaDatabase />}
                      onClick={() => handleImportFromUpload(selectedUpload)}
                      isLoading={importing}
                    >
                      Import Tests from File
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.200">
            <HStack spacing={3} flexWrap="wrap">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {selectedUpload?.lab_id && (
                <Button
                  variant="outline"
                  colorScheme="teal"
                  leftIcon={<FaArrowRight />}
                  onClick={() => {
                    onClose();
                    navigate(`/admin/lab-test-costs/${selectedUpload.lab_id}`);
                  }}
                >
                  View Lab Test Costs
                </Button>
              )}
              <Button
                colorScheme="blue"
                leftIcon={<FaEye />}
                onClick={() => handleStatusUpdate(selectedUpload?.id, 'in_review', processingNotes)}
              >
                Mark In Review
              </Button>
              <Button
                colorScheme="green"
                leftIcon={<FaCheck />}
                onClick={() => handleStatusUpdate(selectedUpload?.id, 'processed', processingNotes)}
              >
                Mark Processed
              </Button>
              <Button
                colorScheme="red"
                leftIcon={<FaTimes />}
                onClick={() => handleStatusUpdate(selectedUpload?.id, 'rejected', processingNotes)}
              >
                Reject
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* File Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg="blue.50" borderBottom="1px solid" borderColor="gray.200">
            <Flex align="center">
              <Icon as={FaEye} color="blue.600" mr={2} />
              <Text>File Preview: {selectedUpload?.file_name}</Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            {previewLoading ? (
              <VStack spacing={4} py={8}>
                <Spinner size="lg" color="blue.500" thickness="4px" />
                <Text color="gray.600">Loading file preview...</Text>
              </VStack>
            ) : filePreview ? (
              <VStack spacing={4} align="stretch">
                <Card bg="gray.50" p={4}>
                  <VStack align="stretch" spacing={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.600">File Information</Text>
                    <Text fontSize="sm" color="gray.700">
                      <strong>Lab:</strong> {selectedUpload?.lab_name}
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      <strong>File:</strong> {filePreview.name}
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      <strong>Type:</strong> {filePreview.type || 'Unknown'}
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      <strong>Size:</strong> {formatFileSize(selectedUpload?.file_size || 0)}
                    </Text>
                  </VStack>
                </Card>
                
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    <Text mb={2} fontWeight="semibold">File Preview</Text>
                    <Text fontSize="sm" mb={3}>
                      Click the link below to view or download the file. For CSV/Excel files, you can open them in a spreadsheet application.
                    </Text>
                    <Link 
                      href={filePreview.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      color="blue.600"
                      fontWeight="semibold"
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                    >
                      <Icon as={FaExternalLinkAlt} />
                      Open File in New Tab
                    </Link>
                  </AlertDescription>
                </Alert>

                {selectedUpload?.file_url && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">File URL:</Text>
                    <Code p={2} borderRadius="md" fontSize="xs" display="block" whiteSpace="pre-wrap" wordBreak="break-all">
                      {selectedUpload.file_url}
                    </Code>
                  </Box>
                )}
              </VStack>
            ) : (
              <Text color="gray.500">No preview available</Text>
            )}
          </ModalBody>
          <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.200">
            <HStack spacing={3}>
              <Button variant="outline" onClick={onPreviewClose}>
                Close
              </Button>
              {filePreview && (
                <Button
                  colorScheme="blue"
                  leftIcon={<FaDownload />}
                  onClick={() => selectedUpload && handleDownload(selectedUpload)}
                >
                  Download File
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Import Results Modal */}
      <Modal isOpen={isImportOpen} onClose={onImportClose} size="lg" closeOnOverlayClick={!importing}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg="purple.50" borderBottom="1px solid" borderColor="gray.200">
            <Flex align="center" gap={2}>
              <Icon as={FaDatabase} color="purple.600" />
              <Text>Import Tests & Panels</Text>
            </Flex>
          </ModalHeader>
          <ModalBody py={6}>
            {importing ? (
              <VStack spacing={4}>
                <Spinner size="xl" color="purple.500" thickness="4px" />
                <Text fontWeight="semibold" color="gray.700">Importing tests and panels...</Text>
                <Box w="100%">
                  <Progress value={importProgress} colorScheme="purple" size="lg" borderRadius="md" />
                  <Text fontSize="sm" color="gray.600" mt={2} textAlign="center">
                    {Math.round(importProgress)}% complete
                  </Text>
                </Box>
              </VStack>
            ) : importResults ? (
              <VStack spacing={4} align="stretch">
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    <Text fontWeight="semibold">Import Completed!</Text>
                  </AlertDescription>
                </Alert>

                <Card bg="gray.50" p={4}>
                  <VStack spacing={3} align="stretch">
                    <HStack justify="space-between">
                      <Text fontWeight="semibold" color="gray.700">Tests Created:</Text>
                      <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                        {importResults.testsCreated}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold" color="gray.700">Panels Created:</Text>
                      <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
                        {importResults.panelsCreated}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold" color="gray.700">Test Offerings Created:</Text>
                      <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                        {importResults.testOfferingsCreated}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold" color="gray.700">Panel Offerings Created:</Text>
                      <Badge colorScheme="teal" fontSize="md" px={3} py={1}>
                        {importResults.panelOfferingsCreated}
                      </Badge>
                    </HStack>
                  </VStack>
                </Card>

                {importResults.errors && importResults.errors.length > 0 && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>
                      <Text fontWeight="semibold" mb={2}>Errors ({importResults.errors.length}):</Text>
                      <VStack align="stretch" spacing={1} maxH="200px" overflowY="auto">
                        {importResults.errors.map((error, idx) => (
                          <Text key={idx} fontSize="sm" color="gray.700">
                            • {error}
                          </Text>
                        ))}
                      </VStack>
                    </AlertDescription>
                  </Alert>
                )}

                {selectedUpload?.lab_id && (
                  <Button
                    colorScheme="teal"
                    leftIcon={<FaArrowRight />}
                    onClick={() => {
                      onImportClose();
                      navigate(`/admin/lab-test-costs/${selectedUpload.lab_id}`);
                    }}
                    w="100%"
                  >
                    View Lab Test Costs
                  </Button>
                )}
              </VStack>
            ) : (
              <VStack spacing={4}>
                <Spinner size="xl" color="purple.500" />
                <Text>Preparing import...</Text>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.200">
            <Button onClick={onImportClose} isDisabled={importing}>
              {importResults ? 'Close' : 'Cancel'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default AdminUploadManager;
